import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { useEffect, useRef, useState } from 'react';
import {
  type Control,
  type DeepPartialSkipArrayKey,
  type FieldPath,
  type FieldPathValue,
  type FieldPathValues,
  type FieldValues,
  type Path,
  type PathValue,
  useWatch,
} from 'react-hook-form';

type UseWatchDebounceSingleFnProp<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TFieldName;
  defaultValue?: FieldPathValue<TFieldValues, TFieldName>;
  control?: Control<TFieldValues>;
  disabled?: boolean;
  exact?: boolean;
  debounceMs?: number;
  onChange?: (value: FieldPathValue<TFieldValues, TFieldName>) => void;
};

type UseWatchDebounceManyFnProp<
  TFieldValues extends FieldValues = FieldValues,
  TFieldNames extends readonly FieldPath<TFieldValues>[] = readonly FieldPath<TFieldValues>[],
> = {
  name: readonly [...TFieldNames];
  defaultValue?: DeepPartialSkipArrayKey<TFieldValues>;
  control?: Control<TFieldValues>;
  disabled?: boolean;
  exact?: boolean;
  debounceMs?: number;
  onChange?: (value: FieldPathValues<TFieldValues, TFieldNames>) => void;
};

export type UseWatchName<
  TFieldValues extends FieldValues = FieldValues,
  TFieldNames extends readonly FieldPath<TFieldValues>[] = readonly FieldPath<TFieldValues>[],
> = readonly [...TFieldNames];

/**
 * React hook to watch a react-hook-form field value with debounce.
 * Returns the value only after it has remained unchanged for the specified debounce period.
 * Optionally, an `onChange` callback can be triggered with the debounced value.
 *
 * @param props - Parameters for the hook.
 * @param props.name - Name/path of the field to watch.
 * @param props.defaultValue - Default value for the field.
 * @param props.control - RHF control object.
 * @param props.disabled - If true, disables watching.
 * @param props.exact - If true, uses exact comparison.
 * @param props.debounceMs - Debounce delay in milliseconds.
 * @param props.onChange - Callback invoked with the debounced value.
 * @returns The debounced value of the watched field.
 *
 * @example
 * 1. const debouncedValue = useWatchDebounce({ name: <path_to_observed_prop> });
 * 2. useWatchDebounce({ name: <path_to_observed_prop>, onChange: (debouncedValue) => void })
 */
export const useWatchDebounce = <
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  debounceMs = 300,
  onChange,
  ...props
}: UseWatchDebounceSingleFnProp<TFieldValues, TFieldName>): PathValue<TFieldValues, TFieldName> => {
  const watchedProp = useWatch<TFieldValues, TFieldName>(props);
  type Value = typeof watchedProp;
  const [value, setValue] = useState<Value>(watchedProp);
  const valueRef = useRef<Value>(watchedProp);

  const debounceHandler = useRef(
    debounce((nextValue: Value) => {
      if (isEqual(valueRef.current, nextValue)) {
        return;
      }

      valueRef.current = nextValue;
      setValue(nextValue);

      if (onChange) {
        onChange(nextValue);
      }
    }, debounceMs)
  );

  useEffect(() => {
    debounceHandler.current(watchedProp);
  }, [watchedProp]);

  useEffect(
    () => () => {
      debounceHandler.current.cancel();
    },
    []
  );

  return value as Value;
};

/**
 * React hook to watch multiple react-hook-form field values with debounce.
 * Returns the values only after they have remained unchanged for the specified debounce period.
 * Optionally, an `onChange` callback can be triggered with the debounced values.
 *
 * @param props - Parameters for the hook.
 * @param props.name - Array of names/paths of the fields to watch.
 * @param props.defaultValue - Default values for the fields.
 * @param props.control - RHF control object.
 * @param props.disabled - If true, disables watching.
 * @param props.exact - If true, uses exact comparison.
 * @param props.debounceMs - Debounce delay in milliseconds.
 * @param props.onChange - Callback invoked with the debounced values.
 * @returns The debounced values of the watched fields.
 *
 * @example
 * 1. const debouncedValues = useWatchManyDebounce({ name: [<path_to_field1>, <path_to_field2>] });
 * 2. useWatchManyDebounce({ name: [<path_to_field1>, <path_to_field2>], onChange: (debouncedValues) => void })
 */
export const useWatchManyDebounce = <
  TFieldValues extends FieldValues,
  TFieldNames extends readonly FieldPath<TFieldValues>[] = readonly FieldPath<TFieldValues>[],
>({
  debounceMs = 300,
  onChange,
  ...props
}: UseWatchDebounceManyFnProp<TFieldValues, TFieldNames>): {
  [K in keyof TFieldNames]: PathValue<TFieldValues, TFieldNames[K] & Path<TFieldValues>>;
} => {
  const watchedProp = useWatch<TFieldValues, TFieldNames>({ ...props });
  type Value = typeof watchedProp;
  const [value, setValue] = useState<Value>(
    watchedProp.map((value, index) =>
      value !== undefined ? value : get(props.defaultValue, props.name[index], undefined)
    ) as Value
  );
  const valueRef = useRef<Value>(watchedProp);

  const debounceHandler = useRef(
    debounce((nextValue: Value) => {
      if (isEqual(valueRef.current, nextValue)) {
        return;
      }

      valueRef.current = nextValue;
      setValue(nextValue);

      if (onChange) {
        onChange(nextValue);
      }
    }, debounceMs)
  );

  useEffect(() => {
    debounceHandler.current(watchedProp);
  }, [watchedProp]);

  useEffect(
    () => () => {
      debounceHandler.current.cancel();
    },
    []
  );

  return value;
};
